import React, {useEffect, useCallback, useState, useRef} from 'react';
import {action} from 'mobx';
import {observer} from 'mobx-react';
import './storepage.scss';
import {ActiveFilterSummary} from './components/active-filters/active-filter-summary';
import {Filters} from './components/filters/filters';
import filtersStore from './components/filters/filters-store/filters-store';
import {ProductListStatus} from './components/product-list-status/product-list-status';
import {getStoreProductsStatusText, isResultsBusy, shouldShowEmptyResults} from './components/product-list-status/store-products-status';
import {formatProductResultSummary, productResultPageCount, shouldRequestStorePage, shouldShowProductPagination} from './components/product-results/format-product-result-summary';
import {storeGridClickPosition} from './components/product-results/grid-click/store-grid-click-position';
import {StoreProductPagination} from './components/product-results/store-product-pagination';
import s from './storepage-datasource';
import {Portal} from '../util';
import {screenResizeStore} from '../global/store/screen-resize-store';
import {getDiscountedPrice} from './pricing';
import {StoreProductCardHeading} from './components/product-card-heading/store-product-card-heading';
import {StoreProductCardImage} from './components/product-card-image/store-product-card-image';
import {StoreProductRating, getStoreProductRatingAriaDescribedBy} from './components/product-rating/product-rating';
import {sigEvent} from '../global/sig';
import {bindStoreSelectionHistory} from './components/filters/url/store-selection';

declare var __store_h1: string;
declare var __store_h1_subText: string;
declare var __is_my_frames: boolean;
declare var __fast_ship_text: string;
declare var __fast_ship_text_mobile: string;
declare var __store_has_h1_subText: boolean;
declare var __fastShipIds: number[];
declare var _msrp_red: boolean;
declare var __price_slash_active: boolean;
declare var StoreID: string;

/** Matches `$size-lg` in storepage.scss — mobile filter drawer below this width. */
const FILTER_DRAWER_MAX_WIDTH = 992;

function getCardRatingDescribedBy(productId: number, reviewCount: number, allowReviews: boolean): string | undefined {
    return getStoreProductRatingAriaDescribedBy(productId, {
        reviewCount,
        isMyFrames: __is_my_frames,
        portalId: Portal?.PortalID,
        allowReviews,
    });
}

export const StorePageComponent: React.FC = observer(() => {
    const [isFilterMenuOpen, setIsFilterMenuOpen] = useState(false);
    const menuToggleRef = useRef<HTMLButtonElement>(null);
    const isDesktopFilters = screenResizeStore.width > FILTER_DRAWER_MAX_WIDTH;

    useEffect(() => {
        filtersStore.loadInitialResults();
    }, []);

    useEffect(() => bindStoreSelectionHistory(filtersStore), []);

    // Clear drawer-open state at desktop so aria-expanded stays accurate and
    // the absolute-positioned drawer styles do not linger across a resize.
    useEffect(() => {
        if (isDesktopFilters) {
            setIsFilterMenuOpen(false);
        }
    }, [isDesktopFilters]);

    const htmlDecode = useCallback((input: string): string => {
        const doc = new DOMParser().parseFromString(input, 'text/html');

        return doc.documentElement.textContent || '';
    }, []);

    const closeFilters = useCallback(() => {
        setIsFilterMenuOpen(false);
        // Defer so the drawer hides before restoring focus.
        requestAnimationFrame(() => {
            menuToggleRef.current?.focus();
        });
    }, []);

    const toggleMenu = useCallback(() => {
        if (isFilterMenuOpen) {
            closeFilters();
        } else {
            setIsFilterMenuOpen(true);
        }
    }, [isFilterMenuOpen, closeFilters]);

    useEffect(() => {
        if (!isFilterMenuOpen || isDesktopFilters) {
            return;
        }

        const onKeyDown = (e: KeyboardEvent) => {
            if (e.key === 'Escape') {
                e.preventDefault();
                closeFilters();
                return;
            }

            if (e.key !== 'Tab') {
                return;
            }

            const drawer = document.getElementById('store-filters-container');
            if (!drawer) {
                return;
            }

            const focusable = Array.from(
                drawer.querySelectorAll<HTMLElement>(`
                    a[href], area[href], input:not([disabled]),
                    select:not([disabled]), textarea:not([disabled]),
                    button:not([disabled]), iframe, object,
                    embed, [tabindex]:not([tabindex="-1"]), [contenteditable="true"]
                `),
            ).filter(el => !el.hasAttribute('disabled') && !el.getAttribute('aria-hidden'));

            if (focusable.length === 0) {
                return;
            }

            const first = focusable[0];
            const last = focusable[focusable.length - 1];
            const active = document.activeElement;

            if (e.shiftKey) {
                if (active === first) {
                    e.preventDefault();
                    last.focus();
                }
            } else if (active === last) {
                e.preventDefault();
                first.focus();
            }
        };

        document.addEventListener('keydown', onKeyDown);

        return () => {
            document.removeEventListener('keydown', onKeyDown);
        };
    }, [isFilterMenuOpen, isDesktopFilters, closeFilters]);

    const onPageClick = useCallback(async (selected: number) => {
        // Read the live stores. The callback is created once, and a failed page leaves the request ahead of the accepted page.
        const accepted = s.acceptedPageNumber ?? 1;
        if (!shouldRequestStorePage(selected, filtersStore.filters.pageNumber, accepted, filtersStore.loading)) {
            return;
        }

        await action(async () => {
            await filtersStore.updateFilter('pageNumber', selected + 1);
            s.getFilterOptions();
            window.scrollTo({top: 0, behavior: 'smooth'});
        })();
    }, []);

    const onPagerChange = useCallback(
        (selected: number) => {
            onPageClick(selected);
            setTimeout(s.stripPaginationAriaAttributes, 150);
        },
        [onPageClick],
    );

    const onRetry = useCallback(() => {
        filtersStore.retry();
    }, []);

    const hasAcceptedResults = filtersStore.hasLoadedSuccessfully && s.acceptedPageNumber != null;
    const acceptedPage = s.acceptedPageNumber ?? 1;
    const shownCount = s.hasProducts ? s.products.hits.hits.length : 0;
    const totalCount = s.hasProducts ? s.products.hits.total.value : 0;
    const resultSummary = hasAcceptedResults
        ? formatProductResultSummary({
              page: acceptedPage,
              total: totalCount,
              visibleCount: shownCount,
          })
        : '';
    const showPagination = shouldShowProductPagination(totalCount);
    const pageCount = productResultPageCount(totalCount);

    const productListStatus = {
        loading: filtersStore.loading,
        hasLoadedSuccessfully: filtersStore.hasLoadedSuccessfully,
        errorMessage: filtersStore.errorMessage,
        hasProducts: !!s.hasProducts,
        shownCount,
        totalCount,
        acceptedPage,
    };

    return (
        <main
            id="store-special-container"
            className={filtersStore.loading ? 'loading' : ''}
        >
            <Filters
                isOpen={isFilterMenuOpen}
                onClose={closeFilters}
            />

            <div
                id="products-status"
                aria-live="polite"
                aria-atomic="true"
                className="sr-only"
            >
                {getStoreProductsStatusText(productListStatus)}
            </div>

            <section
                id="products"
                aria-label="Products"
                aria-busy={isResultsBusy(productListStatus)}
            >
                <header className="row">
                    <div className="col text-center mt-3 mb-2">
                        {__store_h1 && <h1 dangerouslySetInnerHTML={{__html: htmlDecode(__store_h1)}}></h1>}
                        {__store_has_h1_subText && (
                            <div
                                className="sub-h1"
                                dangerouslySetInnerHTML={{__html: htmlDecode(__store_h1_subText)}}
                            ></div>
                        )}
                    </div>
                </header>
                <div className="text-center">
                    <button
                        type="button"
                        id="menu-toggle"
                        ref={menuToggleRef}
                        aria-label={isFilterMenuOpen ? 'Close filter menu' : 'Open filter menu'}
                        aria-expanded={isFilterMenuOpen}
                        aria-controls="store-filters-container"
                        onClick={toggleMenu}
                    >
                        Filter{' '}
                        <span style={{marginLeft: 6}}>
                            <i
                                className="fas fa-sliders"
                                aria-hidden={true}
                            ></i>
                        </span>
                    </button>
                </div>
                <ActiveFilterSummary />
                {!resultSummary || productListStatus.errorMessage ? (
                    <ProductListStatus
                        loading={productListStatus.loading}
                        hasLoadedSuccessfully={productListStatus.hasLoadedSuccessfully}
                        errorMessage={productListStatus.errorMessage}
                        hasProducts={productListStatus.hasProducts}
                        onRetry={onRetry}
                    />
                ) : null}
                {resultSummary && (
                    <div className="product-results-header">
                        {!productListStatus.errorMessage && (
                            <ProductListStatus
                                loading={productListStatus.loading}
                                hasLoadedSuccessfully={productListStatus.hasLoadedSuccessfully}
                                errorMessage={productListStatus.errorMessage}
                                hasProducts={productListStatus.hasProducts}
                                onRetry={onRetry}
                            />
                        )}
                        <p
                            className="product-results-summary"
                            aria-hidden="true"
                        >
                            {resultSummary}
                        </p>
                        {showPagination && (
                            <StoreProductPagination
                                placement="top"
                                pageNumber={acceptedPage}
                                pageCount={pageCount}
                                loading={filtersStore.loading}
                                hrefBuilder={filtersStore.paginationHrefBuilder}
                                onPageChange={onPagerChange}
                            />
                        )}
                    </div>
                )}
                {s.hasProducts && (
                    <>
                        <div id="products-container">
                            {s.products.hits.hits.map((p, tileIndex) => (
                                <div
                                    key={p._id}
                                    className="flex-item"
                                >
                                    <a
                                        href={p._source.fsi.URL}
                                        data-ga4-product-id={p._source.ProductID}
                                        data-ga4-item-id={p._source.ItemID}
                                        data-ga4-name={p._source.Name}
                                        data-ga4-price={p._source.fsi.Price_Retail}
                                        aria-describedby={getCardRatingDescribedBy(p._source.ProductID, p._source.reviewCount, s.allow_reviews)}
                                        onClick={() => sigEvent('store_grid_click', p._source.ProductID, {store_id: StoreID, position: storeGridClickPosition(acceptedPage, tileIndex)})}
                                    >
                                        <div className="prod-image">
                                            {!__is_my_frames && !p._source.hideAisleA && Portal?.PortalID !== 3 && __fastShipIds.includes(p._source.ProductID) && (
                                                <div
                                                    className="store-highlight-container"
                                                    data-product={p._source.ProductID}
                                                >
                                                    <span className={`store-highlight fast-shipping-${Portal?.PortalID}`}>
                                                        <span className="d-md-none">
                                                            {__fast_ship_text_mobile}{' '}
                                                            <i
                                                                className={Portal?.PortalID !== 3 ? `fas fa-shipping-fast` : `fas fa-cart-shopping-fast`}
                                                                aria-hidden={true}
                                                            ></i>
                                                        </span>
                                                        <span className="d-none d-sm-none d-md-inline-block d-lg-inline-block d-xl-inline-block">
                                                            {__fast_ship_text}{' '}
                                                            <i
                                                                className={Portal?.PortalID !== 3 ? `fas fa-shipping-fast` : `fas fa-cart-shopping-fast`}
                                                                aria-hidden={true}
                                                            ></i>
                                                        </span>
                                                    </span>
                                                </div>
                                            )}

                                            {p._source.is_tassel_cord && (
                                                <div
                                                    className="store-highlight-container"
                                                    data-product={p._source.ProductID}
                                                >
                                                    <span className="store-highlight store-highlight-cord-lg">
                                                        <span>Also Holds An Honor Cord</span>
                                                    </span>
                                                    <span className="store-highlight store-highlight-cord-sm">
                                                        <span>Also Holds Honor Cord</span>
                                                    </span>
                                                </div>
                                            )}

                                            <div className="image-container">
                                                <StoreProductCardImage
                                                    productId={p._source.ProductID}
                                                    name={p._source.Name}
                                                    alt=""
                                                    customizationMethod={p._source.CustomizationMethod}
                                                    imageFull={p._source.Image_Full}
                                                    imageThumb={p._source.Image_Thumb}
                                                    imageItemId={p._source.ImageItemID}
                                                    cdnDomain={Portal?.CDNDomain}
                                                    storePageWidth={p._source.StorePageWidth}
                                                    storePageHeight={p._source.StorePageHeight}
                                                    storePageWidthXs={p._source.StorePageWidthXS}
                                                    storePageHeightXs={p._source.StorePageHeightXS}
                                                />

                                                {p._source.hasNewTag && Portal?.PortalID !== 3 && (
                                                    <div className="badge">
                                                        <span>New</span>
                                                    </div>
                                                )}
                                            </div>
                                        </div>

                                        <StoreProductCardHeading
                                            instName={p._source.fsi.InstName}
                                            productName={p._source.Name}
                                            gradSchoolId={p._source.fsi.GradSchoolID}
                                            gsName={p._source.fsi.GSName}
                                        />

                                        {__is_my_frames && (
                                            <div className="price">
                                                <div>
                                                    Wholesale: <span className="price">${p._source.fsi.Price_Wholesale}</span>
                                                </div>
                                                <div>
                                                    Retail: <span className="price">${p._source.fsi.Price_Retail}</span>
                                                </div>
                                            </div>
                                        )}

                                        {!__is_my_frames && !__price_slash_active && (
                                            <div className="price">
                                                <div>
                                                    <span className="price">
                                                        <span className="sr-only">Price </span>${p._source.fsi.priceMSRP}
                                                    </span>
                                                </div>
                                            </div>
                                        )}

                                        {!__is_my_frames && __price_slash_active && (
                                            <div className="price">
                                                <div>
                                                    <span className={_msrp_red ? 'price-discount msrp-red' : 'price-discount'}>
                                                        <span className="sr-only">Sale price </span>
                                                        {getDiscountedPrice(p._source.fsi.priceMSRPDec, __price_slash_active)}
                                                    </span>
                                                    <span className="price-msrp">
                                                        <span className="sr-only">Regular price </span>
                                                        <del>${p._source.fsi.priceMSRP}</del>
                                                    </span>
                                                </div>
                                            </div>
                                        )}

                                        <div className="sku">Item # {p._source.ItemID}</div>

                                        {getCardRatingDescribedBy(p._source.ProductID, p._source.reviewCount, s.allow_reviews) && (
                                            <StoreProductRating
                                                productId={p._source.ProductID}
                                                reviewScore={p._source.reviewScore}
                                                reviewCount={p._source.reviewCount}
                                            />
                                        )}
                                    </a>

                                    <hr
                                        aria-hidden="true"
                                        className="show-small"
                                    />
                                </div>
                            ))}
                        </div>

                        {showPagination && (
                            <StoreProductPagination
                                placement="bottom"
                                pageNumber={acceptedPage}
                                pageCount={pageCount}
                                loading={filtersStore.loading}
                                hrefBuilder={filtersStore.paginationHrefBuilder}
                                onPageChange={onPagerChange}
                            />
                        )}

                        <div className="col d-none d-md-block">
                            <hr />
                        </div>
                    </>
                )}

                {shouldShowEmptyResults(productListStatus) && <h2 className="text-center w-100">{Portal?.PortalID === 3 ? 'Please try another search so we can help you frame what matters most.' : 'No results found, please try another search.'}</h2>}
            </section>
        </main>
    );
});
